import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Session {
  _id?: string;
  id?: string;
  school_id: string;
  session: string;
  start_date: string;
  end_date: string;
  start_month: number;
  end_month: number;
  is_current: boolean;
}

interface SessionModalProps {
  visible: boolean;
  onHide: () => void;
  onSuccess: () => void;
  session?: Session | null;
  defaultSchoolId: string;
  toast: React.RefObject<Toast | null>;
}

// ── Static Data ───────────────────────────────────────────────────────────────

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(0, i).toLocaleString('default', { month: 'long' }),
}));

// Session name options: "2010-11", "2011-12", ... "2039-40"
const sessionOptions = Array.from({ length: 2040 - 2010 }, (_, i) => {
  const startYear = 2010 + i;
  const endYear = startYear + 1;
  return `${startYear}-${String(endYear).slice(-2)}`;
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const getSessionId = (session: Session): string => session._id || session.id || '';

const getMonthDateRange = (monthValue: number, session: string): { start: Date; end: Date } | null => {
  if (!monthValue || !session) return null;
  const startYear = parseInt(session.split('-')[0], 10);
  if (isNaN(startYear)) return null;
  // If start_month <= end_month within same calendar year assumption isn't reliable,
  // so we infer the year based on whether monthValue is the session's start_month or end_month
  // handled by caller passing correct year directly instead.
  return null;
};

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label style={{ display: 'block', marginBottom: '7px', color: '#374151', fontWeight: '600', fontSize: '13px', letterSpacing: '0.01em' }}>
    {children}{required && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
  </label>
);

const TextInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    style={{
      width: '100%', padding: '10px 13px', border: '1.5px solid #e5e7eb', borderRadius: '9px',
      fontSize: '14px', color: '#111827', background: '#fff', outline: 'none', boxSizing: 'border-box',
      transition: 'border-color 0.18s', ...props.style,
    }}
    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; props.onFocus?.(e); }}
    onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; props.onBlur?.(e); }}
  />
);

const SelectInput: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select
    {...props}
    style={{
      width: '100%', padding: '10px 13px', border: '1.5px solid #e5e7eb', borderRadius: '9px',
      fontSize: '14px', color: '#111827', background: '#fff', outline: 'none', cursor: 'pointer',
      transition: 'border-color 0.18s', ...props.style,
    }}
    onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; }}
    onBlur={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
  />
);

// ── Main Component ─────────────────────────────────────────────────────────────

export default function SessionModal({ visible, onHide, onSuccess, session, defaultSchoolId, toast }: SessionModalProps) {
  const isEdit = Boolean(session && getSessionId(session));

  const [formData, setFormData] = useState({
    school_id: defaultSchoolId,
    session: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
    start_month: 4, // Default to April
    end_month: 3,   // Default to March
    is_current: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setError(null);
      if (session) {
        setFormData({
          school_id: session.school_id || defaultSchoolId,
          session: session.session || '',
          start_date: session.start_date ? new Date(session.start_date) : null,
          end_date: session.end_date ? new Date(session.end_date) : null,
          start_month: session.start_month || 4,
          end_month: session.end_month || 3,
          is_current: session.is_current || false,
        });
      } else {
        setFormData({
          school_id: defaultSchoolId,
          session: '',
          start_date: null,
          end_date: null,
          start_month: 4,
          end_month: 3,
          is_current: false,
        });
      }
    }
  }, [visible, session, defaultSchoolId]);

  // Auto-compute start_date (1st of start_month) and end_date (last day of end_month)
  // whenever session name, start_month, or end_month changes.
  useEffect(() => {
    const { session: sessName, start_month, end_month } = formData;
    if (!sessName || !start_month || !end_month) return;

    const startYear = parseInt(sessName.split('-')[0], 10);
    if (isNaN(startYear)) return;

    // If end_month is before start_month (e.g. April -> March), the end falls in the next year.
    const endYear = end_month < start_month ? startYear + 1 : startYear;

    const newStartDate = new Date(startYear, start_month - 1, 1);
    // Last day of end_month/endYear: day 0 of the following month
    const newEndDate = new Date(endYear, end_month, 0);

    setFormData((p) => {
      const sameStart = p.start_date && p.start_date.getTime() === newStartDate.getTime();
      const sameEnd = p.end_date && p.end_date.getTime() === newEndDate.getTime();
      if (sameStart && sameEnd) return p;
      return { ...p, start_date: newStartDate, end_date: newEndDate };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.session, formData.start_month, formData.end_month]);

  const handleSave = async () => {
    const { school_id, session: sessName, start_date, end_date, start_month, end_month, is_current } = formData;

    if (!school_id || !sessName || !start_date || !end_date) {
      setError('Please fill all required fields.');
      return;
    }

    if (start_date >= end_date) {
      setError('End date must be after start date.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        school_id,
        session: sessName,
        start_date: start_date.toISOString(),
        end_date: end_date.toISOString(),
        start_month,
        end_month,
        is_current,
      };

      let response;
      if (isEdit) {
        const sessionId = getSessionId(session!);
        response = await apiClient.put(`/api/sessions/${sessionId}`, payload);
      } else {
        response = await apiClient.post('/api/sessions', payload);
      }

      const data = response.data;
      if (data.status === 'success' || data.message) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: isEdit ? 'Session updated successfully!' : 'Session created successfully!',
          life: 4000,
        });
        onSuccess();
      } else {
        setError(data.error || data.message || 'Failed to save session.');
      }
    } catch (err: any) {
      const status = err.response?.status;
      const body = err.response?.data;
      if (status === 401) setError('Authentication failed. Please login again.');
      else if (status === 400) setError(body?.error || body?.details || 'Invalid data provided.');
      else setError(body?.error || err.message || 'An error occurred while saving.');
    } finally {
      setLoading(false);
    }
  };

  const modalHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '10px', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`pi ${isEdit ? 'pi-pencil' : 'pi-plus'}`} style={{ fontSize: '18px', color: 'white' }} />
      </div>
      <div>
        <div style={{ color: 'white', fontWeight: '700', fontSize: '17px', lineHeight: 1.2 }}>
          {isEdit ? 'Edit Session' : 'Add New Session'}
        </div>
        {isEdit && (
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '2px' }}>
            {session?.session}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        .p-dialog.session-unified-modal .p-dialog-header {
          background: linear-gradient(135deg, #1e3a8a 0%, #2855ae 60%, #3b82f6 100%) !important;
          color: white !important; border-radius: 16px 16px 0 0 !important; padding: 18px 24px !important;
        }
        .p-dialog.session-unified-modal .p-dialog-header .p-dialog-header-icon { color: rgba(255,255,255,0.7) !important; }
        .p-dialog.session-unified-modal .p-dialog-header .p-dialog-header-icon:hover { color: white !important; background: rgba(255,255,255,0.15) !important; }
        .p-dialog.session-unified-modal .p-dialog-content { padding: 0 !important; border-radius: 0 0 16px 16px !important; overflow: hidden; }
        .p-dialog.session-unified-modal { border-radius: 16px !important; box-shadow: 0 24px 64px rgba(0,0,0,0.18) !important; }
        .p-calendar .p-inputtext { width: 100%; padding: 10px 13px; border: 1.5px solid #e5e7eb; border-radius: 9px; font-size: 14px; }
        .p-calendar .p-inputtext:focus { border-color: #3b82f6 !important; }
      `}</style>

      <Dialog
        header={modalHeader}
        visible={visible}
        onHide={onHide}
        style={{ width: '600px', maxWidth: '94vw' }}
        draggable={false}
        resizable={false}
        className="session-unified-modal"
        footer={null}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '75vh' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              <div>
                <FieldLabel required>School ID</FieldLabel>
                <TextInput
                  type="text"
                  value={formData.school_id}
                  onChange={(e) => setFormData((p) => ({ ...p, school_id: e.target.value }))}
                  placeholder="e.g., 65a000000000000000000001"
                  disabled
                />
              </div>

              <div>
                <FieldLabel required>Session Name</FieldLabel>
                <SelectInput
                  value={formData.session}
                  onChange={(e) => setFormData((p) => ({ ...p, session: e.target.value }))}
                >
                  <option value="" disabled>Select session</option>
                  {sessionOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </SelectInput>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <FieldLabel required>Start Date</FieldLabel>
                  <Calendar
                    value={formData.start_date}
                    onChange={(e) => setFormData((p) => ({ ...p, start_date: e.value as Date }))}
                    dateFormat="dd M yy"
                    showIcon
                  />
                </div>
                <div>
                  <FieldLabel required>End Date</FieldLabel>
                  <Calendar
                    value={formData.end_date}
                    onChange={(e) => setFormData((p) => ({ ...p, end_date: e.value as Date }))}
                    dateFormat="dd M yy"
                    showIcon
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <FieldLabel required>Start Month</FieldLabel>
                  <SelectInput
                    value={formData.start_month}
                    onChange={(e) => setFormData((p) => ({ ...p, start_month: Number(e.target.value) }))}
                  >
                    {monthOptions.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </SelectInput>
                </div>
                <div>
                  <FieldLabel required>End Month</FieldLabel>
                  <SelectInput
                    value={formData.end_month}
                    onChange={(e) => setFormData((p) => ({ ...p, end_month: Number(e.target.value) }))}
                  >
                    {monthOptions.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </SelectInput>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#f9fafb', borderRadius: '10px', border: '1.5px solid #e5e7eb' }}>
                <input
                  type="checkbox"
                  id="is_current"
                  checked={formData.is_current}
                  onChange={(e) => setFormData((p) => ({ ...p, is_current: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#10b981' }}
                />
                <label htmlFor="is_current" style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#374151', margin: 0 }}>
                  Set as Current Active Session
                </label>
              </div>

              {error && (
                <div style={{
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '9px',
                  padding: '11px 14px', color: '#dc2626', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <i className="pi pi-exclamation-circle" /> {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                <button
                  onClick={onHide}
                  disabled={loading}
                  style={{
                    flex: 1, padding: '12px', background: '#f3f4f6', border: '1.5px solid #e5e7eb',
                    borderRadius: '10px', cursor: 'pointer', color: '#374151', fontSize: '14px', fontWeight: '600',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    flex: 2, padding: '12px',
                    background: loading ? '#93c5fd' : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer',
                    color: 'white', fontSize: '14px', fontWeight: '600', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', gap: '8px',
                    boxShadow: loading ? 'none' : '0 4px 14px rgba(59,130,246,0.35)',
                  }}
                >
                  {loading ? (
                    <><i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }} /> Saving...</>
                  ) : isEdit ? (
                    <><i className="pi pi-check" style={{ fontSize: '14px' }} /> Update Session</>
                  ) : (
                    <><i className="pi pi-plus" style={{ fontSize: '14px' }} /> Create Session</>
                  )}
                </button>
              </div>

            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}